import React, { useEffect, useState } from 'react'
import { Container, Typography, Grid2, Paper, FormControl, FormControlLabel, Checkbox, FormGroup, FormHelperText, Skeleton, Link, Alert } from '@mui/material'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { IconChevronLeft } from '@tabler/icons-react'
import { useForm, Controller } from 'react-hook-form';
import axios from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useParams, useLocation, useNavigate } from 'react-router-dom'

import CustomButton from '@components/button/customButton'
import ControlledTextField from '@components/inputs/controlledTextField'
import ConfirmDialog from '@components/dialog/confirmDialog'
import InfoDialog from '@components/dialog/infoDialog'

import { useSnackbar } from '@context/snackbarContext'

import { UserApiEndpoints } from '@configuration/apiEndpoints'

import "./featuredRequest.css";

export default function FeaturedRequest() {
  const { t } = useTranslation()
  const openSnackbar = useSnackbar();
  const { pathname } = useLocation();
  const { id } = useParams()
  const navigate = useNavigate()

  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [infoDialogConfig, setInfoDialogConfig] = useState({
    open: false,
    dialogTitle: t('payment-page.acceptanceNoValidated'),
    dialogDescription: t('payment-page.acceptanceNoValidatedDesc'),
  })

  const { handleSubmit, control, getValues, reset, watch, setValue } = useForm({
    defaultValues: {
      donorName: '',
      contactName: '',
      contactPhone: '',
      contactEmail: '',
      contactAddress: '',
      isCorrect: false
    }
  })

  const { isFetching, error, data, isError } = useQuery({
    queryKey: ['getFeaturedDonationByGuid', id],
    queryFn: () =>
      axios.get(`${UserApiEndpoints.getFeaturedDonationByGuid}/${id}`, { requiresAuth: false }),
    initialData: null,
    select: (res) => {
      const data = res?.data || null
      return data
    },
  })

  const registerFeaturedDonationMutation = useMutation({
    mutationFn: (data) => {
      return axios.post(`${UserApiEndpoints.registerFeaturedDonation}/${id}`, data,  {headers: { requiresAuth: false }})
    },
    onSuccess: (res) => {
      reset()
      setOpenConfirmDialog(false)
      setInfoDialogConfig({
        open: true,
        dialogTitle: t('featured-page.requestSubmitted'),
        dialogDescription: t('featured-page.requestSubmittedDesc'),
      })
    },
    onError: (err) => {
      setOpenConfirmDialog(false)
      openSnackbar(t('featured-page.submitFailed'), "error")
    }
  })

  const onSubmit = () => {
    setOpenConfirmDialog(true)
  };

  const onConfirmSubmit = () => {
    const data = getValues()

    const formData = {
      donorName: data?.donorName.trim(),
      contactName: data?.contactName.trim(),
      contactPhone: data?.contactPhone.trim(),
      contactEmail: data?.contactEmail.trim(),
      contactAddress: data?.contactAddress.trim(),
    }

    // console.log(formData)
    // return

    registerFeaturedDonationMutation.mutate(formData)
      
  }

  useEffect(()=>{
    if(!id){
      navigate(`/featured`, { replace: true })
    }
  }, [id])

  const isSkeleton = !(data)

  const disabledPayment = false

  return (
    <Container className='payment' maxWidth="md">
      <Link onClick={()=>navigate(`/featured/`, { replace: true })} underline="hover" color='#414042' fontSize={'14px'} style={{display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer'}}>
        <IconChevronLeft size={15}/>{t('activity-page.back')}
      </Link>
      {isError && 
        <Alert severity="error" style={{marginTop: 20}}>
          {t('featured-page.unableToRetrieve')}
        </Alert>
      }
      <Typography className='title'>
        {isSkeleton ? <Skeleton variant='text' animation={!isError && 'pulse'}/> : data ? data[`header${t('param')}`] : '-'}
      </Typography>
      <Typography className='description'>
        {isSkeleton ? <Skeleton variant='text' animation={!isError && 'pulse'}/> : data ? data[`tagline${t('param')}`] : '-'}
      </Typography>
      <form onSubmit={handleSubmit(onSubmit)} className='content-wrapper'>
        {isSkeleton ?
          <Skeleton animation={!isError && 'pulse'} variant="rounded" height={"450px"} style={{borderRadius: '10px'}}/>
        :
          <Paper className='content-card'>
            <Grid2 container spacing={'25px'}>
              <Typography className='content-title'>{t('payment-page.donationDetails')}</Typography>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="donorName"
                    control={control}
                    label={t('featured-page.benefactorName')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('featured-page.benefactorNameRequired')
                    }}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="contactName"
                    control={control}
                    label={t('featured-page.contactName')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('featured-page.contactNameRequired')
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="contactPhone"
                    control={control}
                    label={t('featured-page.contactNumber')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('featured-page.contactNumberRequired'),
                      pattern: {
                        value: /^[0-9]+$/,
                        message: t('register-page.contactNumberInvalid')
                      }
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="contactEmail"
                    control={control}
                    label={t('featured-page.contactEmail')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('featured-page.contactEmailRequired'),
                      pattern: {
                        value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                        message: t('payment-page.emailAddressInvalid'),
                      }
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>

              <Grid2 size={12}>
                <Grid2 size={{ lg: 6, md: 6,  sm: 8, xs: 12 }}>
                  <ControlledTextField 
                    name="contactAddress"
                    control={control}
                    label={t('featured-page.mailingAddress')}
                    variant='standard'
                    size='medium'
                    rules={{
                      required: t('featured-page.mailingAddressRequired')
                    }}
                    // disabled={fetchFundMutation.isPending || fetchFundMutation.isError}
                  />
                </Grid2>
              </Grid2>
            
            </Grid2>
          </Paper>
        }
        {isSkeleton ?
          <Skeleton animation={!isError && 'pulse'} variant="rounded" height={"100px"} style={{borderRadius: '10px'}}/>
        :
          <Paper className='content-card'>
            <Grid2 container spacing={'10px'}>
              <Grid2 size={12}>
                <Grid2 size={12}>
                  <Controller
                    name={'isCorrect'}
                    control={control}
                    defaultValue={false}
                    rules={{ required: t('payment-page.agreeCorrectInfoRequired') }}
                    render={({ field, fieldState: { error } }) => (
                      <FormControl
                        error={error}
                        fullWidth
                      >
                        <FormGroup>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  {...field}
                                  checked={field.value}
                                  size='small'
                                />
                              }
                              label={
                                <Typography fontSize={'14px'}>
                                  {t('featured-page.agreeCorrectInfo')}
                                </Typography>
                              }
                            />
                        </FormGroup>
                        {error &&
                          <FormHelperText variant='standard'>{error.message}</FormHelperText>
                        }
                      </FormControl>
                    )}
                  />
                </Grid2>
              </Grid2>
            </Grid2>
          </Paper>
        }
        {isSkeleton ?
          <Skeleton animation={!isError && 'pulse'} variant="rounded" height={"100px"} style={{borderRadius: '10px'}}/>
        :
          <Paper className='content-card'>
            <CustomButton 
              type="submit" 
              disabled={disabledPayment} 
              loading={registerFeaturedDonationMutation.isPending}
            >
              {t('register-page.submit')}
            </CustomButton>
          </Paper>
        }
      </form>
      <ConfirmDialog 
        open={openConfirmDialog}
        onClose={()=>setOpenConfirmDialog(false)}
        onSubmit={onConfirmSubmit}
        dialogTitle={t('featured-page.submitRequest')}
        dialogDescription={t('featured-page.submitRequestConfirmation')}
        cancelText={t('cancel')}
        confirmText={t('confirm')}
        loading={registerFeaturedDonationMutation.isPending}
      />
      <InfoDialog 
        open={infoDialogConfig.open}
        onClose={()=>setInfoDialogConfig(prev => ({...prev, open: false}))}
        dialogTitle={infoDialogConfig.dialogTitle}
        dialogDescription={infoDialogConfig.dialogDescription}
        closeText={t('ok')}
      />
    </Container>
  )
}
